import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_FILTER_HOVER_CLASS } from '@/components/ui/form-control-styles'
import { Label } from '@/components/ui/label'
import { DatePicker } from '@/components/shared/DatePicker'
import { cn } from '@/lib/utils'

interface Props {
  from?: string
  to?: string
  onChange: (next: { date_from?: string; date_to?: string }) => void
  /** i18n key under the `clients` namespace. Defaults to tab "Date" label. */
  labelKey?: string
  /** Visible label. Overrides `labelKey` when set (e.g. contracts Due Date). */
  label?: string
  /** Figma list toolbar uses a field-style trigger without a chevron. */
  appearance?: 'button' | 'field'
  /** Extra classes merged onto the field/button trigger. */
  triggerClassName?: string
  /** Optional controlled open state for coordinating with sibling filters. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

const fieldTriggerClass =
  'inline-flex h-10 items-center gap-2 rounded-xl border border-[#e7e9ef] bg-white px-4 py-3 text-sm font-normal leading-none text-[#171a20] transition-colors focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [&_svg]:size-4'

// "Date" filter button + popover with a created-date From/To range. Used by the
// client Quotes (2.4.7) and Contracts (2.4.8) tabs and the client list toolbar.
export function DateRangeFilter({
  from,
  to,
  onChange,
  labelKey = 'tabFilters.date',
  label: labelProp,
  appearance = 'button',
  triggerClassName,
  open: controlledOpen,
  onOpenChange,
}: Props) {
  const { t } = useTranslation('clients')
  const [internalOpen, setInternalOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const active = Boolean(from || to)
  const open = controlledOpen ?? internalOpen

  const setOpen = useCallback(
    (nextOpen: boolean) => {
      if (controlledOpen === undefined) setInternalOpen(nextOpen)
      onOpenChange?.(nextOpen)
    },
    [controlledOpen, onOpenChange]
  )

  const updatePosition = () => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setPosition({ top: rect.bottom + 4, left: rect.left })
  }

  useLayoutEffect(() => {
    if (!open) return
    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      if (
        target instanceof Element &&
        target.closest('[data-radix-popper-content-wrapper]')
      ) {
        return
      }
      setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open, setOpen])

  // Clear an invalid range when from moves past to (e.g. stale URL params).
  useEffect(() => {
    if (from && to && from > to) {
      onChange({ date_from: from, date_to: undefined })
    }
  }, [from, to, onChange])

  const label = labelProp ?? t(labelKey)

  const panel =
    open &&
    createPortal(
      <div
        ref={panelRef}
        className='fixed z-50 w-64 space-y-3 rounded-md border border-border bg-popover p-3 shadow-md'
        style={{ top: position.top, left: position.left }}
      >
        <div className='w-full space-y-1'>
          <Label className='text-xs'>{t('tabFilters.dateFrom')}</Label>
          <DatePicker
            value={from ?? ''}
            max={to || undefined}
            onChange={value => {
              const date_from = value || undefined
              let date_to = to || undefined
              if (date_from && date_to && date_from > date_to) {
                date_to = undefined
              }
              onChange({ date_from, date_to })
            }}
          />
        </div>
        <div className='w-full space-y-1'>
          <Label className='text-xs'>{t('tabFilters.dateTo')}</Label>
          <DatePicker
            value={to ?? ''}
            min={from || undefined}
            onChange={value => {
              const date_to = value || undefined
              if (date_to && from && date_to < from) return
              onChange({ date_from: from || undefined, date_to })
            }}
          />
        </div>
        {active && (
          <button
            type='button'
            onClick={() =>
              onChange({ date_from: undefined, date_to: undefined })
            }
            className='text-xs font-medium text-primary hover:underline'
          >
            {t('tabFilters.clear')}
          </button>
        )}
      </div>,
      document.body
    )

  return (
    <div className='shrink-0'>
      {appearance === 'field' ? (
        <button
          ref={triggerRef}
          type='button'
          onClick={() => setOpen(!open)}
          className={cn(
            fieldTriggerClass,
            triggerClassName,
            FORM_FILTER_HOVER_CLASS,
            active && 'text-primary [&_svg]:text-primary'
          )}
        >
          <Calendar
            className='size-4 shrink-0 text-[#272c36]'
            strokeWidth={1.6}
          />
          <span className='whitespace-nowrap'>{label}</span>
        </button>
      ) : (
        <Button
          ref={triggerRef}
          type='button'
          variant='filter'
          onClick={() => setOpen(!open)}
          className={cn(
            triggerClassName,
            FORM_FILTER_HOVER_CLASS,
            active && 'text-primary'
          )}
        >
          <Calendar className='mr-2 size-4' />
          {label}
        </Button>
      )}
      {panel}
    </div>
  )
}
